import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ChevronDown, Filter, History, Search, UsersRound } from "lucide-react";
import { EmptyState, Skeleton } from "@wedding/ui";
import { apiFetchPage } from "../api";
import { modules } from "../config";

type AuditEntry = {
  timestamp: string;
  workspaceId: string;
  userId: string;
  operation: string;
  entityType: string;
  entityId: string;
  before?: unknown;
  after?: unknown;
  source?: string;
  actor?: { label: string; email?: string };
};

const operationLabels: Record<string, string> = {
  create: "创建",
  update: "修改",
  delete: "删除",
  backup: "创建备份",
  restore: "恢复备份",
  "reset-to-seed": "重置项目",
  "update-wedding-date": "修改婚期",
  "add-member": "添加协作者",
  "update-member": "修改协作者",
  "remove-member": "移除协作者",
};

const entityLabels = Object.fromEntries(
  modules.map((module) => [module.type, module.singular]),
);
entityLabels.workspace = "婚礼项目";
entityLabels["workspace-member"] = "协作者";
const moduleByType = Object.fromEntries(
  modules.map((module) => [module.type, module]),
);

function actorLabel(entry: AuditEntry) {
  if (entry.actor?.label) return entry.actor.label;
  if (entry.userId === "dev-user") return "本地项目所有者";
  if (entry.userId === "rsvp-public") return "宾客公开登记 / 回复";
  return "项目成员";
}

function sourceLabel(entry: AuditEntry) {
  return entry.source === "ai" ? "AI 助手" : "人工操作";
}

function unwrap(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const object = value as Record<string, unknown>;
  if (
    object.data &&
    typeof object.data === "object" &&
    !Array.isArray(object.data)
  ) {
    return object.data as Record<string, unknown>;
  }
  if (
    object.settings &&
    typeof object.settings === "object" &&
    !Array.isArray(object.settings)
  ) {
    return object.settings as Record<string, unknown>;
  }
  return object;
}

function titleFor(entry: AuditEntry) {
  const data = { ...unwrap(entry.before), ...unwrap(entry.after) };
  const configuredFirstKey = moduleByType[entry.entityType]?.fields[0]?.key;
  const keys = [
    "title",
    "vendorName",
    "projectName",
    "name",
    "item",
    "risk",
    "idea",
    "segment",
    "shotName",
    "role",
    configuredFirstKey,
  ].filter(Boolean) as string[];
  for (const key of keys) {
    if (data[key] != null && String(data[key]).trim()) return String(data[key]);
  }
  return entityLabels[entry.entityType] ?? entry.entityType;
}

function sameValue(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function displayValue(value: unknown, fieldKey: string, entityType: string) {
  if (value == null || value === "") return "未填写";
  if (value === true) return "是";
  if (value === false) return "否";
  const field = moduleByType[entityType]?.fields.find(
    (item) => item.key === fieldKey,
  );
  if (field?.type === "currency") {
    return new Intl.NumberFormat("zh-CN", {
      style: "currency",
      currency: "CNY",
    }).format(Number(value) / 100);
  }
  if (Array.isArray(value)) return value.map(String).join("、") || "空列表";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return field?.optionLabels?.[String(value)] ?? String(value);
}

function changesFor(entry: AuditEntry) {
  const before = unwrap(entry.before);
  const after = unwrap(entry.after);
  const configuredFields = moduleByType[entry.entityType]?.fields ?? [];
  const configuredKeys = configuredFields.map((field) => field.key);
  const fallbackKeys = [
    ...new Set([...Object.keys(before), ...Object.keys(after)]),
  ].filter(
    (key) =>
      ![
        "originalData",
        "rsvpTokenHash",
        "rsvpTokenCreatedAt",
        "rsvpHistory",
      ].includes(key),
  );
  const keys = configuredKeys.length ? configuredKeys : fallbackKeys;
  return keys
    .filter((key) => !sameValue(before[key], after[key]))
    .map((key) => ({
      key,
      label: configuredFields.find((field) => field.key === key)?.label ?? key,
      before: displayValue(before[key], key, entry.entityType),
      after: displayValue(after[key], key, entry.entityType),
    }));
}

export function AuditPage() {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string;
  };
  const [search, setSearch] = useState("");
  const [operation, setOperation] = useState("");
  const [actor, setActor] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const audit = useInfiniteQuery({
    queryKey: [workspaceId, "audit"],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      apiFetchPage<AuditEntry[]>(
        `/workspaces/${workspaceId}/audit${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    getNextPageParam: (lastPage) => lastPage.meta.cursor,
  });

  const allEntries = useMemo(
    () => audit.data?.pages.flatMap((page) => page.data) ?? [],
    [audit.data],
  );
  const actors = useMemo(
    () =>
      [...new Set(allEntries.map(actorLabel))].sort((a, b) =>
        a.localeCompare(b, "zh-CN"),
      ),
    [allEntries],
  );

  const entries = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return [...allEntries]
      .sort((left, right) => right.timestamp.localeCompare(left.timestamp))
      .filter((entry) => {
        const text =
          `${titleFor(entry)} ${entityLabels[entry.entityType] ?? entry.entityType} ${actorLabel(entry)} ${sourceLabel(entry)} ${operationLabels[entry.operation] ?? entry.operation}`.toLowerCase();
        const day = entry.timestamp.slice(0, 10);
        return (
          (!keyword || text.includes(keyword)) &&
          (!operation || entry.operation === operation) &&
          (!actor || actorLabel(entry) === actor) &&
          (!dateFrom || day >= dateFrom) &&
          (!dateTo || day <= dateTo)
        );
      });
  }, [actor, allEntries, dateFrom, dateTo, operation, search]);

  return (
    <div className="page audit-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">操作记录</span>
          <h1>操作记录</h1>
          <p>查看谁在什么时间，对哪条业务记录做了什么，以及具体改动内容。</p>
        </div>
      </header>
      <section className="toolbar audit-toolbar">
        <div className="search-box">
          <Search size={17} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="搜索记录名称、模块或操作人…"
            aria-label="搜索操作记录"
          />
        </div>
        <label className="filter-select">
          <Filter size={16} />
          <select
            value={operation}
            onChange={(event) => setOperation(event.target.value)}
            aria-label="筛选操作类型"
          >
            <option value="">全部操作</option>
            {Object.entries(operationLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-select">
          <UsersRound size={16} />
          <select
            value={actor}
            onChange={(event) => setActor(event.target.value)}
            aria-label="按操作人筛选"
          >
            <option value="">全部操作人</option>
            {actors.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
        <label className="audit-date-filter">
          <span>从</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            aria-label="操作开始日期"
          />
        </label>
        <label className="audit-date-filter">
          <span>到</span>
          <input
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            aria-label="操作结束日期"
          />
        </label>
        {(search || operation || actor || dateFrom || dateTo) && (
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setOperation("");
              setActor("");
              setDateFrom("");
              setDateTo("");
            }}
          >
            清除筛选
          </button>
        )}
        <span className="audit-count">
          已加载 {allEntries.length} 条 · 当前显示 {entries.length} 条
        </span>
      </section>
      <section className="data-panel">
        {audit.isLoading ? (
          <Skeleton lines={8} />
        ) : audit.isError ? (
          <div className="error-state">
            <h3>操作记录加载失败</h3>
            <p>{audit.error.message}</p>
            <button onClick={() => audit.refetch()}>重试</button>
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            icon={<History size={30} />}
            title={allEntries.length ? "没有符合条件的记录" : "还没有操作记录"}
            detail={
              allEntries.length
                ? "请调整搜索词、日期或筛选条件。"
                : "创建、修改或删除记录后，操作详情会显示在这里。"
            }
          />
        ) : (
          <div className="audit-list">
            {entries.map((entry) => {
              const changes = changesFor(entry);
              const operationLabel =
                operationLabels[entry.operation] ?? entry.operation;
              const entityLabel =
                entityLabels[entry.entityType] ?? entry.entityType;
              return (
                <details
                  className="audit-entry"
                  key={`${entry.timestamp}-${entry.entityId}-${entry.operation}`}
                >
                  <summary>
                    <span className={`audit-op ${entry.operation}`}>
                      {operationLabel}
                    </span>
                    {entry.source === "ai" && (
                      <span className="audit-source ai">AI 操作</span>
                    )}
                    <div className="audit-summary">
                      <strong>{titleFor(entry)}</strong>
                      <p>
                        {entityLabel} ·{" "}
                        {entry.source === "ai" ? "AI 代操作 · " : ""}
                        {actorLabel(entry)} · {changes.length} 处变更
                      </p>
                    </div>
                    <time dateTime={entry.timestamp}>
                      {new Date(entry.timestamp).toLocaleString("zh-CN")}
                    </time>
                    <ChevronDown className="audit-chevron" size={17} />
                  </summary>
                  <div className="audit-detail">
                    <dl className="audit-meta">
                      <div>
                        <dt>操作人</dt>
                        <dd>{actorLabel(entry)}</dd>
                      </div>
                      <div>
                        <dt>操作来源</dt>
                        <dd>{sourceLabel(entry)}</dd>
                      </div>
                      <div>
                        <dt>业务模块</dt>
                        <dd>{entityLabel}</dd>
                      </div>
                      <div>
                        <dt>记录编号</dt>
                        <dd>{entry.entityId}</dd>
                      </div>
                      <div>
                        <dt>成员编号</dt>
                        <dd>{entry.userId}</dd>
                      </div>
                    </dl>
                    {changes.length > 0 ? (
                      <div className="audit-changes">
                        <div className="audit-change-head">
                          <span>字段</span>
                          <span>修改前</span>
                          <span>修改后</span>
                        </div>
                        {changes.map((change) => (
                          <div className="audit-change-row" key={change.key}>
                            <strong>{change.label}</strong>
                            <pre>{change.before}</pre>
                            <pre>{change.after}</pre>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="audit-no-change">
                        此操作没有可展示的业务字段变化。
                      </p>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        )}
        {audit.hasNextPage && (
          <div className="load-more-row">
            <button
              onClick={() => void audit.fetchNextPage()}
              disabled={audit.isFetchingNextPage}
            >
              {audit.isFetchingNextPage ? "正在加载…" : "加载更早的操作记录"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
